import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { 
  Search,
  Package,
  Truck,
  ArrowRight,
  AlertTriangle,
  Loader2,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  FileText,
  Beaker,
  Factory,
  TrendingUp,
  Activity
} from 'lucide-react';
import { formatQuantidadeComUnidade } from '../utils/numberFormat';

/** Backend pode enviar array (legado) ou objeto (ex.: parametros_registrados nas etapas_executadas). */
function textoEsperadoValidacao(val) {
  const vmin = val?.valor_minimo;
  const vmax = val?.valor_maximo;
  if (vmin != null && vmin !== '' && vmax != null && vmax !== '') {
    return `${vmin} – ${vmax}`;
  }
  return val?.valor_esperado;
}

function normalizarValidacoesColetadas(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    return Object.entries(raw).map(([nome, valor]) => ({
      nome,
      valor_coletado: valor != null && typeof valor === 'object' ? JSON.stringify(valor) : String(valor ?? '—'),
      valor_esperado: '—',
      conforme: true
    }));
  }
  return [];
}

/**
 * Formata datas vindas do callable: ISO string, Timestamp serializado ({seconds}|{_seconds}),
 * objeto com toDate(), Date, número (ms).
 */
function formatarDataPtBR(val, comHora = false) {
  if (val == null || val === '') return 'N/A';
  try {
    let d;
    if (typeof val === 'object' && val !== null) {
      if (typeof val.toDate === 'function') {
        d = val.toDate();
      } else if (typeof val.seconds === 'number') {
        d = new Date(val.seconds * 1000);
      } else if (typeof val._seconds === 'number') {
        d = new Date(val._seconds * 1000);
      } else if (val instanceof Date) {
        d = val;
      } else {
        return 'N/A';
      }
    } else if (typeof val === 'string') {
      d = new Date(val);
    } else if (typeof val === 'number') {
      d = new Date(val);
    } else {
      return 'N/A';
    }
    if (Number.isNaN(d.getTime())) return 'N/A';
    return comHora
      ? d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : d.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
  } catch {
    return 'N/A';
  }
}

const RastreabilidadePage = () => {
  const { hasPermission, getEmpresaId } = useAuth();
  const [activeTab, setActiveTab] = useState('fornecedor'); // fornecedor, fabricado
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Busca por lote fornecedor
  const [loteFornecedor, setLoteFornecedor] = useState('');
  const [resultadoFornecedor, setResultadoFornecedor] = useState(null);
  
  // Busca por lote fabricado
  const [loteFabricado, setLoteFabricado] = useState('');
  const [resultadoFabricado, setResultadoFabricado] = useState(null);
  
  // Listas de lotes disponíveis
  const [lotesFornecedor, setLotesFornecedor] = useState([]);
  const [lotesFabricado, setLotesFabricado] = useState([]);

  useEffect(() => {
    loadLotesFornecedor();
    loadLotesFabricado();
  }, []);

  const loadLotesFornecedor = async () => {
    try {
      const listarLotesRastreabilidade = httpsCallable(functions, 'listarLotesRastreabilidade');
      const result = await listarLotesRastreabilidade({
        empresa_id: getEmpresaId(),
        tipo: 'fornecedor'
      });

      if (result.data.success) {
        setLotesFornecedor(result.data.data.lotes || []);
      }
    } catch (err) {
      console.error('Erro ao carregar lotes:', err);
    }
  };

  const loadLotesFabricado = async () => {
    try {
      const listarLotesRastreabilidade = httpsCallable(functions, 'listarLotesRastreabilidade');
      const result = await listarLotesRastreabilidade({
        empresa_id: getEmpresaId(),
        tipo: 'fabricado'
      });

      if (result.data.success) {
        setLotesFabricado(result.data.data.lotes || []);
      }
    } catch (err) {
      console.error('Erro ao carregar lotes:', err);
    }
  };

  const handleBuscarLoteFornecedor = async () => {
    if (!loteFornecedor.trim()) {
      setError('Informe o lote do fornecedor');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResultadoFornecedor(null);
      
      const rastrearPorLoteFornecedor = httpsCallable(functions, 'rastrearPorLoteFornecedor');
      const result = await rastrearPorLoteFornecedor({
        empresa_id: getEmpresaId(),
        lote_fornecedor: loteFornecedor.trim()
      });

      if (result.data.success) {
        if (result.data.data.encontrado) {
          setResultadoFornecedor(result.data.data);
        } else {
          setError('Nenhum recebimento encontrado com este lote');
        }
      }
    } catch (err) {
      console.error('Erro ao rastrear:', err);
      setError('Erro ao rastrear lote do fornecedor');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarLoteFabricado = async () => {
    if (!loteFabricado.trim()) {
      setError('Informe o lote de fabricação');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResultadoFabricado(null);
      
      const rastrearPorLoteFabricado = httpsCallable(functions, 'rastrearPorLoteFabricado');
      const result = await rastrearPorLoteFabricado({
        empresa_id: getEmpresaId(),
        lote_fabricacao: loteFabricado.trim()
      });

      if (result.data.success) {
        if (result.data.data.encontrado) {
          setResultadoFabricado(result.data.data);
        } else {
          setError('Nenhum rastro encontrado para este lote (produção ou carrinho de separação).');
        }
      }
    } catch (err) {
      console.error('Erro ao rastrear:', err);
      setError('Erro ao rastrear lote de fabricação');
    } finally {
      setLoading(false);
    }
  };

  if (!hasPermission('view_dashboard')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso Negado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Você não tem permissão para acessar a rastreabilidade.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rastreabilidade</h1>
        <p className="text-gray-600">Histórico de rota das matérias primas, insumos e ingredientes</p>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Activity className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Sistema de Rastreabilidade Bidirecional
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>• <strong>Lote do Fornecedor:</strong> Descubra em quais lotes fabricados uma matéria-prima foi utilizada</p>
              <p>• <strong>Lote de Fabricação:</strong> Descubra a origem completa de todos os ingredientes utilizados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('fornecedor');
              setError('');
              setResultadoFornecedor(null);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'fornecedor'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Truck className="h-5 w-5 mr-2" />
              Rastrear por Lote do Fornecedor
            </div>
          </button>
          
          <button
            onClick={() => {
              setActiveTab('fabricado');
              setError('');
              setResultadoFabricado(null);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'fabricado'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Factory className="h-5 w-5 mr-2" />
              Rastrear por Lote de Fabricação
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'fornecedor' && (
        <TabLoteFornecedor
          loteFornecedor={loteFornecedor}
          setLoteFornecedor={setLoteFornecedor}
          lotesFornecedor={lotesFornecedor}
          onBuscar={handleBuscarLoteFornecedor}
          resultado={resultadoFornecedor}
          loading={loading}
        />
      )}

      {activeTab === 'fabricado' && (
        <TabLoteFabricado
          loteFabricado={loteFabricado}
          setLoteFabricado={setLoteFabricado}
          lotesFabricado={lotesFabricado}
          onBuscar={handleBuscarLoteFabricado}
          resultado={resultadoFabricado}
          loading={loading}
        />
      )}
    </div>
  );
};

// ==================== TAB: RASTREAR LOTE FORNECEDOR ====================

const TabLoteFornecedor = ({ loteFornecedor, setLoteFornecedor, lotesFornecedor, onBuscar, resultado, loading }) => {
  return (
    <div className="space-y-6">
      {/* Busca */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Buscar por Lote do Fornecedor</h3>
        
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={loteFornecedor}
              onChange={(e) => setLoteFornecedor(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onBuscar()}
              className="w-full border border-gray-300 rounded-md px-4 py-2"
              placeholder="Ex: MSP-2025-JAN-15"
            />
          </div>
          <button
            onClick={onBuscar}
            disabled={loading || !loteFornecedor.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Rastreando...
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Rastrear
              </>
            )}
          </button>
        </div>

        {/* Lotes recentes */}
        {lotesFornecedor.length > 0 && !resultado && (
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-2">Lotes recentes:</p>
            <div className="flex flex-wrap gap-2">
              {lotesFornecedor.slice(0, 10).map((lote, idx) => (
                <button
                  key={idx}
                  onClick={() => setLoteFornecedor(lote.lote_fornecedor)}
                  className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full text-gray-700"
                >
                  {lote.lote_fornecedor}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Resultado */}
      {resultado && (
        <div className="space-y-6">
          {/* Info do Recebimento */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <Package className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Recebimento Original</h3>
                <p className="text-sm text-gray-600">Lote: <span className="font-mono font-bold">{resultado.lote_fornecedor}</span></p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Fornecedor:</p>
                <p className="font-medium text-gray-900">{resultado.fornecedor_nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ingrediente:</p>
                <p className="font-medium text-gray-900">{resultado.materia_prima_nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Quantidade Recebida:</p>
                <p className="font-medium text-gray-900">{formatQuantidadeComUnidade(resultado.quantidade_recebida, 'kg')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Data do recebimento:</p>
                <p className="font-medium text-gray-900 flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatarDataPtBR(resultado.data_recebimento, true)}
                </p>
              </div>
            </div>

            {/* Parâmetros de Qualidade */}
            {resultado.parametros_qualidade_recebimento && resultado.parametros_qualidade_recebimento.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Parâmetros de Qualidade:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {resultado.parametros_qualidade_recebimento.map((param, idx) => (
                    <div key={idx} className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-600">{param.nome}</p>
                      <p className="text-sm font-medium text-gray-900">{param.valor_coletado} {param.unidade}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Lotes Fabricados que usaram */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Utilização em Produção</h3>
                <p className="text-sm text-gray-600">
                  {resultado.lotes_fabricados.length} lote(s) fabricado(s) utilizaram este lote
                </p>
              </div>
            </div>

            {resultado.lotes_fabricados.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  Este lote ainda não foi utilizado em nenhuma produção.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {resultado.lotes_fabricados.map((lote, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono text-lg font-bold text-blue-600">
                            {lote.lote_fabricacao}
                          </span>
                          {lote.lote_carrinho && (
                            <span className="text-xs bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded">
                              Carrinho
                            </span>
                          )}
                          <ArrowRight className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{lote.produto_nome}</span>
                        </div>
                        <div className="mt-2 flex gap-4 text-sm text-gray-600">
                          <span>Quantidade: <strong>{formatQuantidadeComUnidade(lote.quantidade_utilizada, 'kg')}</strong></span>
                          <span>Data: {formatarDataPtBR(lote.data_producao)}</span>
                        </div>
                      </div>
                      <div>
                        {lote.status === 'concluida' ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-6 w-6 text-yellow-600" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TAB: RASTREAR LOTE FABRICADO ====================

const TabLoteFabricado = ({ loteFabricado, setLoteFabricado, lotesFabricado, onBuscar, resultado, loading }) => {
  return (
    <div className="space-y-6">
      {/* Busca */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Buscar por Lote de Fabricação</h3>
        
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={loteFabricado}
              onChange={(e) => setLoteFabricado(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onBuscar()}
              className="w-full border border-gray-300 rounded-md px-4 py-2"
              placeholder="Ex: 1/2025"
            />
          </div>
          <button
            onClick={onBuscar}
            disabled={loading || !loteFabricado.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Rastreando...
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Rastrear
              </>
            )}
          </button>
        </div>

        {/* Lotes recentes */}
        {lotesFabricado.length > 0 && !resultado && (
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-2">Lotes recentes:</p>
            <div className="flex flex-wrap gap-2">
              {lotesFabricado.slice(0, 10).map((lote, idx) => (
                <button
                  key={`${lote.lote_fabricacao}-${lote.carrinho_id || 'ordem'}-${idx}`}
                  type="button"
                  onClick={() => setLoteFabricado(lote.lote_fabricacao)}
                  className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full text-gray-700 inline-flex items-center gap-1"
                >
                  {lote.tipo_lote === 'carrinho' && (
                    <span className="text-emerald-700 font-medium">[Carrinho]</span>
                  )}
                  {lote.lote_fabricacao} — {lote.produto_nome || '—'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Resultado */}
      {resultado && (
        <div className="space-y-6">
          {/* Info da Ordem de Produção */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <Factory className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Ordem de Produção</h3>
                <p className="text-sm text-gray-600">
                  Lote:{' '}
                  <span className="font-mono font-bold text-blue-600">{resultado.lote_fabricacao}</span>
                  {resultado.origem_rastreio === 'carrinho' && (
                    <span className="ml-2 text-xs font-normal text-emerald-700">
                      (rastreio pelo lote do carrinho de separação)
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Produto:</p>
                <p className="font-medium text-gray-900">{resultado.produto_nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Versão:</p>
                <p className="font-medium text-gray-900">{resultado.produto_versao}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bateladas:</p>
                <p className="font-medium text-gray-900">{resultado.numero_bateladas}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Início da produção:</p>
                <p className="font-medium text-gray-900">{formatarDataPtBR(resultado.data_inicio, true)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Conclusão:</p>
                <p className="font-medium text-gray-900">{formatarDataPtBR(resultado.data_conclusao, true)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status:</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  resultado.status === 'concluida' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {resultado.status === 'concluida' ? 'Concluída' : resultado.status}
                </span>
              </div>
            </div>
          </div>

          {/* Ingredientes utilizados */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <Beaker className="h-8 w-8 text-purple-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Ingredientes Utilizados</h3>
                <p className="text-sm text-gray-600">Origem completa com rastreabilidade</p>
              </div>
            </div>

            <div className="space-y-4">
              {resultado.materias_primas_utilizadas.map((mp, idx) => (
                <div key={idx} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{mp.materia_prima_nome}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Quantidade: <strong>{formatQuantidadeComUnidade(mp.quantidade_utilizada, mp.unidade)}</strong>
                      </p>
                      {mp.lote_carrinho && (
                        <p className="text-xs text-emerald-800 mt-1 font-mono">
                          Vinculado ao lote do carrinho: {mp.lote_carrinho}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white p-3 rounded border border-gray-200">
                    <div>
                      <p className="text-xs text-gray-600">Lote do Fornecedor:</p>
                      <p className="font-mono text-sm font-bold text-blue-600">{mp.lote_fornecedor}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Fornecedor:</p>
                      <p className="text-sm font-medium text-gray-900">{mp.fornecedor_nome}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Data Fabricação MP:</p>
                      <p className="text-sm text-gray-900">{formatarDataPtBR(mp.data_fabricacao_mp)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Data Validade MP:</p>
                      <p className="text-sm text-gray-900">{formatarDataPtBR(mp.data_validade_mp)}</p>
                    </div>
                  </div>

                  {/* Parâmetros de Qualidade da MP */}
                  {mp.parametros_qualidade && mp.parametros_qualidade.length > 0 && (
                    <div className="mt-3 bg-blue-50 p-3 rounded border border-blue-200">
                      <p className="text-xs font-medium text-blue-900 mb-2">Parâmetros de Qualidade no Recebimento:</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {mp.parametros_qualidade.map((param, pidx) => (
                          <div key={pidx}>
                            <p className="text-xs text-blue-700">{param.nome}</p>
                            <p className="text-sm font-medium text-blue-900">{param.valor_coletado} {param.unidade}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Processo de Fabricação */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <Activity className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Processo de Fabricação</h3>
                <p className="text-sm text-gray-600">Validações coletadas em cada etapa</p>
              </div>
            </div>

            <div className="space-y-4">
              {(resultado.processo_fabricacao || []).map((etapa, idx) => {
                const validacoesLista = normalizarValidacoesColetadas(etapa.validacoes_coletadas);
                return (
                <div key={idx} className="border-l-4 border-blue-600 bg-gray-50 p-4 rounded">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                      {etapa.ordem}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{etapa.etapa_nome}</h4>
                      <p className="text-sm text-gray-600">{etapa.etapa_descricao}</p>
                    </div>
                  </div>

                  {/* Validações */}
                  <div className="bg-white p-3 rounded border border-gray-200">
                    <p className="text-xs font-medium text-gray-700 mb-2">Validações / parâmetros coletados:</p>
                    <div className="space-y-1">
                      {validacoesLista.length === 0 ? (
                        <p className="text-sm text-gray-500">Nenhum registro nesta etapa.</p>
                      ) : (
                        validacoesLista.map((val, vidx) => (
                          <div key={vidx} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{val.nome}:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{val.valor_coletado}</span>
                              {textoEsperadoValidacao(val) && textoEsperadoValidacao(val) !== '—' && (
                                <span className="text-xs text-gray-500">({textoEsperadoValidacao(val)})</span>
                              )}
                              {val.conforme && <CheckCircle className="h-4 w-4 text-green-600" />}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                    {etapa.data_fim && (
                      <span>
                        Concluída em:{' '}
                        <strong className="text-gray-700">{formatarDataPtBR(etapa.data_fim, true)}</strong>
                      </span>
                    )}
                    {etapa.operador_nome && <span>Operador: {etapa.operador_nome}</span>}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RastreabilidadePage;


